import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { questionnaireService } from '../services/questionnaireService';
import { questionService } from '../services/questionService';
import { Questionnaire, Question } from '../types';
import { ArrowLeftIcon, CheckCircleIcon } from '../components/Icons';
import { shouldDisplayQuestion } from '../utils/displayLogic';

const QuestionnairePreviewPage: React.FC = () => {
  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  const navigate = useNavigate();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState<Record<number, any>>({});

  useEffect(() => {
    if (questionnaireId) {
      fetchData();
    }
  }, [questionnaireId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [questionnaireData, questionsData] = await Promise.all([
        questionnaireService.getQuestionnaire(Number(questionnaireId)),
        questionService.getQuestions(Number(questionnaireId)),
      ]);
      setQuestionnaire(questionnaireData);
      setQuestions(questionsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSingleChoice = (questionId: number, optionId: number) => {
    setAnswers({
      ...answers,
      [questionId]: optionId,
    });
  };

  const handleMultipleChoice = (questionId: number, optionId: number) => {
    const current = answers[questionId] || [];
    const newValue = current.includes(optionId)
      ? current.filter((id: number) => id !== optionId)
      : [...current, optionId];
    setAnswers({
      ...answers,
      [questionId]: newValue,
    });
  };

  const handleTextInput = (questionId: number, value: string) => {
    setAnswers({
      ...answers,
      [questionId]: value,
    });
  };

  const handleSlider = (questionId: number, value: number) => {
    setAnswers({
      ...answers,
      [questionId]: value,
    });
  };

  const handleMatrix = (questionId: number, row: string, column: string) => {
    const current = answers[questionId] || {};
    setAnswers({
      ...answers,
      [questionId]: {
        ...current,
        [row]: column,
      },
    });
  };

  // 过滤出应该显示的问题
  const visibleQuestions = useMemo(() => {
    return questions.filter(question => shouldDisplayQuestion(question, answers));
  }, [questions, answers]);

  const getSliderSettings = (question: Question) => {
    try {
      if (question.settings) {
        const settings = JSON.parse(question.settings);
        return {
          min: settings.min || 0,
          max: settings.max || 100,
          step: settings.step || 1,
          defaultValue: settings.defaultValue || 50,
        };
      }
    } catch (e) {
      console.error('Failed to parse slider settings:', e);
    }
    return { min: 0, max: 100, step: 1, defaultValue: 50 };
  };

  const getMatrixSettings = (question: Question) => {
    try {
      if (question.settings) {
        console.log('Matrix settings string:', question.settings);
        const settings = JSON.parse(question.settings);
        console.log('Parsed matrix settings:', settings);
        return {
          rows: settings.rows || [],
          columns: settings.columns || [],
          scores: settings.scores || {},
        };
      }
    } catch (e) {
      console.error('Failed to parse matrix settings:', e);
      console.error('Settings string was:', question.settings);
    }
    return { rows: [], columns: [], scores: {} };
  };

  const renderQuestion = (question: Question, index: number) => {
    return (
      <div key={question.id} className="bg-surface-container-lowest rounded-2xl shadow-soft p-6">
        {/* 问题标题 */}
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-medium">
            {index + 1}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-on-surface font-headline">
              {question.title}
              {question.required && <span className="text-danger-600 ml-1">*</span>}
            </h3>
            {question.imageUrl && (
              <div className="mt-3">
                <img
                  src={question.imageUrl}
                  alt="Question"
                  className="max-w-md rounded-xl shadow-soft"
                />
              </div>
            )}
          </div>
        </div>

        {/* 问题内容 */}
        <div className="ml-11">
          {question.type === 'SINGLE_CHOICE' && (
            <div className="space-y-2">
              {question.options.map((option) => (
                <label
                  key={option.id}
                  className="flex items-start gap-3 p-3 bg-surface-container-low rounded-xl hover:bg-surface-container-high cursor-pointer transition-colors"
                >
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    className="mt-1 text-primary-600 focus:ring-primary-500"
                    checked={answers[question.id] === option.id}
                    onChange={() => handleSingleChoice(question.id, option.id)}
                  />
                  <div className="flex-1">
                    <div className="text-on-surface font-body">{option.content}</div>
                    {option.imageUrl && (
                      <img
                        src={option.imageUrl}
                        alt="Option"
                        className="mt-2 max-w-xs rounded-xl shadow-soft"
                      />
                    )}
                    {option.isOtherOption && answers[question.id] === option.id && (
                      <input
                        type="text"
                        className="mt-2 w-full px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                        placeholder="请输入其他内容"
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}

          {question.type === 'MULTIPLE_CHOICE' && (
            <div className="space-y-2">
              {question.options.map((option) => (
                <label
                  key={option.id}
                  className="flex items-start gap-3 p-3 bg-surface-container-low rounded-xl hover:bg-surface-container-high cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    className="mt-1 rounded text-primary-600 focus:ring-primary-500"
                    checked={(answers[question.id] || []).includes(option.id)}
                    onChange={() => handleMultipleChoice(question.id, option.id)}
                  />
                  <div className="flex-1">
                    <div className="text-on-surface font-body">{option.content}</div>
                    {option.imageUrl && (
                      <img
                        src={option.imageUrl}
                        alt="Option"
                        className="mt-2 max-w-xs rounded-xl shadow-soft"
                      />
                    )}
                    {option.isOtherOption && (answers[question.id] || []).includes(option.id) && (
                      <input
                        type="text"
                        className="mt-2 w-full px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                        placeholder="请输入其他内容"
                      />
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}

          {question.type === 'TEXT' && (
            <textarea
              className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
              rows={4}
              value={answers[question.id] || ''}
              onChange={(e) => handleTextInput(question.id, e.target.value)}
              placeholder="请输入您的答案"
            />
          )}

          {question.type === 'SLIDER' && (() => {
            const settings = getSliderSettings(question);
            const value = answers[question.id] !== undefined ? answers[question.id] : settings.defaultValue;
            return (
              <div className="space-y-3">
                <input
                  type="range"
                  min={settings.min}
                  max={settings.max}
                  step={settings.step}
                  value={value}
                  onChange={(e) => handleSlider(question.id, Number(e.target.value))}
                  className="w-full h-2 bg-surface-container-high rounded-xl appearance-none cursor-pointer accent-primary-600"
                />
                <div className="flex justify-between text-sm text-on-surface-variant font-label">
                  <span>{settings.min}</span>
                  <span className="font-medium text-primary-600">{value}</span>
                  <span>{settings.max}</span>
                </div>
              </div>
            );
          })()}

          {question.type === 'DATE' && (
            <input
              type="date"
              className="px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
              value={answers[question.id] || ''}
              onChange={(e) => handleTextInput(question.id, e.target.value)}
            />
          )}

          {question.type === 'SHORT_TEXT' && (
            <input
              type="text"
              className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
              value={answers[question.id] || ''}
              onChange={(e) => handleTextInput(question.id, e.target.value)}
              placeholder="请输入"
              maxLength={(() => {
                try { return JSON.parse(question.settings || '{}').maxLength || 100; } catch { return 100; }
              })()}
            />
          )}

          {question.type === 'DROPDOWN' && (
            <select
              className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
              value={answers[question.id] || ''}
              onChange={(e) => handleSingleChoice(question.id, Number(e.target.value))}
            >
              <option value="">请选择</option>
              {question.options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.content}
                </option>
              ))}
            </select>
          )}

          {question.type === 'MATRIX' && (() => {
            const settings = getMatrixSettings(question);
            if (settings.rows.length === 0 || settings.columns.length === 0) {
              return (
                <div className="text-on-surface-variant text-sm font-body">
                  矩阵题配置不完整，请在编辑页面设置行和列
                </div>
              );
            }
            return (
              <div className="overflow-x-auto">
                <table className="min-w-full" style={{ border: '1px solid rgba(171, 179, 183, 0.2)' }}>
                  <thead>
                    <tr className="bg-surface-container-low">
                      <th className="px-4 py-2 text-left text-sm font-medium text-on-surface font-label" style={{ border: '1px solid rgba(171, 179, 183, 0.2)' }}>

                      </th>
                      {settings.columns.map((column: string, idx: number) => (
                        <th
                          key={idx}
                          className="px-4 py-2 text-center text-sm font-medium text-on-surface font-label"
                          style={{ border: '1px solid rgba(171, 179, 183, 0.2)' }}
                        >
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {settings.rows.map((row: string, rowIdx: number) => (
                      <tr key={rowIdx} className="hover:bg-surface-container-low">
                        <td className="px-4 py-2 text-sm font-medium text-on-surface font-body" style={{ border: '1px solid rgba(171, 179, 183, 0.2)' }}>
                          {row}
                        </td>
                        {settings.columns.map((column: string, colIdx: number) => (
                          <td
                            key={colIdx}
                            className="px-4 py-2 text-center"
                            style={{ border: '1px solid rgba(171, 179, 183, 0.2)' }}
                          >
                            <input
                              type="radio"
                              name={`matrix-${question.id}-${row}`}
                              className="text-primary-600 focus:ring-primary-500"
                              checked={(answers[question.id]?.[row] || '') === column}
                              onChange={() => handleMatrix(question.id, row, column)}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-on-surface mb-2 font-headline">问卷不存在</h2>
          <button
            onClick={() => navigate('/questionnaires')}
            className="text-primary-600 hover:text-primary-700"
          >
            返回列表
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      {/* Header */}
      <div className="bg-surface-container-lowest sticky top-0 z-10 shadow-soft">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate(`/questionnaires/${questionnaireId}`)}
            className="flex items-center text-on-surface-variant hover:text-on-surface mb-2"
          >
            <ArrowLeftIcon size={20} className="mr-2" />
            返回问卷管理
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-on-surface font-headline">{questionnaire.title}</h1>
              {questionnaire.description && (
                <p className="text-on-surface-variant mt-1 font-body">{questionnaire.description}</p>
              )}
            </div>
            <div className="px-3 py-1 bg-primary-100 text-primary-700 rounded-xl text-sm font-medium font-label">
              预览模式
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {questions.length === 0 ? (
          <div className="text-center py-12 bg-surface-container-lowest rounded-3xl shadow-soft">
            <h3 className="text-lg font-medium text-on-surface mb-2 font-headline">暂无问题</h3>
            <p className="text-on-surface-variant mb-4 font-body">请先添加问题再预览</p>
            <button
              onClick={() => navigate(`/questionnaires/${questionnaireId}`)}
              className="inline-flex items-center gap-2 px-4 py-2 gradient-primary text-on-primary rounded-xl hover:opacity-90 shadow-teal"
            >
              返回编辑
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-6">
              {visibleQuestions.map((question, index) => renderQuestion(question, index))}
            </div>

            {/* Submit Button (Preview only) */}
            <div className="mt-8 bg-surface-container-lowest rounded-3xl shadow-soft p-6">
              <button
                onClick={() => {
                  alert('这是预览模式，提交功能在实际问卷中可用');
                }}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 gradient-primary text-on-primary rounded-xl hover:opacity-90 font-medium shadow-teal"
              >
                <CheckCircleIcon size={20} />
                提交问卷
              </button>
              <p className="mt-3 text-center text-sm text-on-surface-variant font-label">
                这是预览模式，提交按钮仅用于展示
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default QuestionnairePreviewPage;
